/**
 * Permission-based Element Visibility
 *
 * Shows/hides elements based on user permissions using data attributes
 */

import type { UserInfo } from '../api/types.js';

/**
 * Check if user has a specific permission
 */
function userHasPermission(user: UserInfo | null, permission: string): boolean {
  if (!user) return false;
  if (user.role === 'admin') return true;
  return user.permissions.includes(permission);
}

/**
 * Check if user has any of the specified permissions
 */
function userHasAnyPermission(user: UserInfo | null, permissions: string[]): boolean {
  return permissions.some(p => userHasPermission(user, p));
}

/**
 * Check if user has all of the specified permissions
 */
function userHasAllPermissions(user: UserInfo | null, permissions: string[]): boolean {
  return permissions.every(p => userHasPermission(user, p));
}

/**
 * Apply permission-based visibility to all elements on the page
 *
 * Supported data attributes:
 * - data-permission="perm"           Show if user has this permission
 * - data-permission-any="p1,p2,p3"   Show if user has any of these
 * - data-permission-all="p1,p2,p3"   Show if user has all of these
 * - data-permission-not="perm"       Show if user does NOT have this permission
 * - data-role="admin"                Show only for admin role
 * - data-authenticated               Show only if user is authenticated
 * - data-unauthenticated             Show only if user is NOT authenticated
 */
export function applyPermissions(user: UserInfo | null): void {
  // data-permission="name"
  document.querySelectorAll('[data-permission]').forEach(el => {
    const permission = el.getAttribute('data-permission')!;
    const hasIt = userHasPermission(user, permission);
    (el as HTMLElement).style.display = hasIt ? '' : 'none';
  });

  // data-permission-any="p1,p2,p3"
  document.querySelectorAll('[data-permission-any]').forEach(el => {
    const permissions = el.getAttribute('data-permission-any')!.split(',').map(p => p.trim());
    const hasAny = userHasAnyPermission(user, permissions);
    (el as HTMLElement).style.display = hasAny ? '' : 'none';
  });

  // data-permission-all="p1,p2,p3"
  document.querySelectorAll('[data-permission-all]').forEach(el => {
    const permissions = el.getAttribute('data-permission-all')!.split(',').map(p => p.trim());
    const hasAll = userHasAllPermissions(user, permissions);
    (el as HTMLElement).style.display = hasAll ? '' : 'none';
  });

  // data-permission-not="name"
  document.querySelectorAll('[data-permission-not]').forEach(el => {
    const permission = el.getAttribute('data-permission-not')!;
    const hasIt = userHasPermission(user, permission);
    (el as HTMLElement).style.display = hasIt ? 'none' : '';
  });

  // data-role="admin"
  document.querySelectorAll('[data-role]').forEach(el => {
    const role = el.getAttribute('data-role')!;
    const hasRole = user?.role === role;
    (el as HTMLElement).style.display = hasRole ? '' : 'none';
  });

  // data-authenticated
  document.querySelectorAll('[data-authenticated]').forEach(el => {
    (el as HTMLElement).style.display = user ? '' : 'none';
  });

  // data-unauthenticated
  document.querySelectorAll('[data-unauthenticated]').forEach(el => {
    (el as HTMLElement).style.display = user ? 'none' : '';
  });
}

/**
 * Disable form elements that require permissions the user doesn't have
 */
export function applyPermissionsToForm(user: UserInfo | null, form: HTMLFormElement): void {
  form.querySelectorAll('[data-permission]').forEach(el => {
    const permission = el.getAttribute('data-permission')!;
    const hasIt = userHasPermission(user, permission);
    if (el instanceof HTMLInputElement || el instanceof HTMLSelectElement || el instanceof HTMLButtonElement) {
      el.disabled = !hasIt;
    }
  });
}
